import React, { useEffect, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import VesselHeader from "../../components/Vessel/VesselHeader";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getIMODCSData } from "../../services/complianceService";
import BenchmarkYearPicker from "../../components/Common/BenchmarkYearPicker";
import ExportToExcel from "../../components/Common/ExportToExcel";

const IMODCS = () => {
  const location = useLocation();
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVesselId, setSelectedVesselId] = useState(null);
  const [mainSpreadsheetData, setMainSpreadsheetData] = useState([[]]);

  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { currentVesselId } = location.state || {};

  const benchmarkYear = useSelector((state) => state.benchmarkYear.year);

  useEffect(() => {
    if (currentVesselId) {
      setSelectedVesselId(currentVesselId);
    } else if (currentVessel?._id) {
      setSelectedVesselId(currentVessel._id);
    }
  }, [currentVesselId, currentVessel?._id]);

  useEffect(() => {
    if (!selectedVesselId) return;

    const fetchIMODCSData = async () => {
      setLoading(true);
      try {
        const res = await getIMODCSData(selectedVesselId, benchmarkYear);

        if (res.status === "success" && Array.isArray(res.data)) {
          const data = res.data;
          setApiData(data);

          // Transform to spreadsheet view
          const transformedData = transformIMODCSData(data);
          setMainSpreadsheetData(transformedData);
        } else {
          console.error("Failed to load IMO DCS data:", res);
          setApiData([]);
        }
      } catch (err) {
        console.error("Error fetching IMO DCS data:", err);
        setApiData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIMODCSData();
  }, [selectedVesselId, benchmarkYear]);

  const transformIMODCSData = (data) => {
    if (!data || data.length === 0) return [[]];

    // Define the exact column order and labels you want to display
    const columnMappings = [
      { label: "Report Start Time (UTC)", key: "reportStartTime" },
      { label: "Report End Time (UTC)", key: "reportEndTime" },
      { label: "Exceptional conditions (Y/N)", key: "exceptionalConditions" },
      { label: "Sailing in ice condition (Y/N)", key: "sailingInIceCondition" },
      { label: "STS Operation (Y/N)", key: "stsOperation" },
      { label: "Cargo Quantity (MT)", key: "cargoQuantityMT" },
      { label: "Distance Sailed (NM)", key: "distanceTravelledNM" },
      {
        label: "Distance Sailed in Laden Condition (NM)",
        key: "distanceTravelledInLadenConditionNM",
      },
      { label: "Transport Work (MT-NM)", key: "transportWorkMTNM" },
      { label: "On Shore Power Supplied", key: "onShorePowerSupplied" },
      {
        label: "Category for innovative energy efficiency",
        key: "categoryForInnovativeEnergyEfficiency",
      },
      { label: "Hours Underway (Hrs)", key: "hoursUnderwayHrs" },
      { label: "Total mass", key: "totalMass" },
      {
        label: "Main Engine Fuel Consumption",
        key: "mainEngineFuelConsumption",
      },
      {
        label: "Auxiliary Engine Fuel Consumption",
        key: "auxiliaryEngineFuelConsumption",
      },
      { label: "Boiler Fuel Consumption", key: "boilerFuelConsumption" },
      {
        label:
          "Consumed for production of electrical power (FCelectrical) Cargo Cooling/reefer/Cargo Heating",
        key: "fcElectrical",
      },
      {
        label:
          "Consumed by oil-fired boiler for cargo heating/discharge on tankers (FC Boiler)",
        key: "fcBoiler",
      },
      {
        label:
          "Consumed by stand-alone engine driven cargo pumps during discharge operations on tankers (FC Others)",
        key: "fcOthers",
      },
    ];

    // Create header row
    const headers = columnMappings.map((col) => ({
      value: col.label,
      readOnly: true,
      className: "header-cell",
    }));

    // Create data rows for all API records
    const rows = data.map((record) => {
      return columnMappings.map((col) => {
        let value = record[col.key];

        // Format date fields
        if (col.key.toLowerCase().includes("time") && value) {
          value = new Date(value).toUTCString();
        }

        // Format numbers to 2 decimals
        if (typeof value === "number") {
          value = value.toFixed(2);
        }

        return {
          value: value?.toString() || "-",
          readOnly: true,
        };
      });
    });

    // Return a 2D array: first row is header, following are data rows
    return [headers, ...rows];
  };

  const handleMainSpreadsheetChange = (data) => {
    setMainSpreadsheetData(data);
  };

  if (loading) {
    return (
      <div className="font-sans text-sm min-h-screen bg-gray-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <VesselHeader pageTitle="IMO DCS Compliance Data" />
          <BenchmarkYearPicker />
        </div>
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading IMO DCS data...</span>
        </div>
      </div>
    );
  }

  if (!apiData || apiData.length === 0) {
    return (
      <div className="font-sans text-sm min-h-screen bg-gray-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-300">
          <VesselHeader pageTitle="IMO DCS Compliance Data" />
          <BenchmarkYearPicker />
        </div>
        <div className="p-8 text-center">
          <p className="text-gray-500">No IMO DCS compliance data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans text-sm min-h-screen bg-gray-50">
      <div className="flex items-center justify-between p-4 border-b border-gray-300">
        <VesselHeader pageTitle="IMO DCS Compliance Data" />
        <div className="flex space-x-2">
          <BenchmarkYearPicker />
          <ExportToExcel />
        </div>
      </div>

      <div className="p-4 space-y-6">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[1200px] p-4">
              <Spreadsheet
                data={mainSpreadsheetData}
                onChange={handleMainSpreadsheetChange}
                hideRowIndicators={true}
                hideColumnIndicators={true}
                className="enhanced-spreadsheet"
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .enhanced-spreadsheet {
          --selected-cell: #e3f2fd;
          --selected-row: #f5f5f5;
          --selected-column: #f5f5f5;
        }
        .enhanced-spreadsheet table {
          border-collapse: separate;
          border-spacing: 0;
          width: auto !important;
        }
        .enhanced-spreadsheet td {
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 8px 12px !important;
          transition: background-color 0.2s;
          white-space: nowrap;
          color: #1f2937 !important;
          background-color: white !important;
        }
        .header-cell {
          background-color: #f1f5f9 !important;
          font-weight: 700 !important;
          color: #374151 !important;
        }
        .enhanced-spreadsheet tr:hover td {
          background-color: #f9fafb !important;
        }
        .enhanced-spreadsheet .active-cell {
          box-shadow: inset 0 0 0 2px #3b82f6 !important;
        }
      `}</style>
    </div>
  );
};

export default IMODCS;
